/* eslint no-constant-condition: ["error", { "checkLoops": false }] */
import { call, put, takeLatest, all, fork } from 'redux-saga/effects';

import history from 'routes/history';
import iclinic from 'services/iclinic';
import filterTerms from 'state/userInfo/utils';
import { handlerErrors } from 'state/shared/sagas';
import { getNextURLParam, getNextUrl } from 'shared/utils/url';
import {
  AUTH_TOKEN_KEY,
  PASSWORD_EXPIRATION_FLOW_KEY,
  USER_ID_KEY,
} from 'shared/constants/auth';
import { SERVICE_TERMS } from 'state/userInfo/constants';
import { dispatchGaEvent } from 'shared/utils/googleAnalytics';
import { dispatchHubspotEvent } from 'shared/utils/hubspot';
import * as types from './constants';
import { LoginResponseData, LoginResponseForm } from './types';
import {
  authorize,
  unauthorize,
  setToken,
  removeToken,
  loginWithEmailFailure,
  loginWithGoogleAuth2Success,
  loginWithGoogleAuth2Failure,
  showCaptcha,
  hideCaptcha,
  loginWithEmailSubmit,
  authenticateFailure,
} from './actions';
import { errorsMessages } from './errors';
import { socialSignupSuccess } from '../signup/state/index';
import { getSocialSignupInfo } from '../signup/services';
import { SocialPayload } from '../signup/types';
import { getUtmsData } from '../signup/utils';
import { captureException } from 'shared/utils/handlerErrors';

export function* workerUnauthorize(action) {
  yield put(unauthorize());
  yield put(removeToken());
  localStorage.removeItem(AUTH_TOKEN_KEY);
  let nextURL = process.env.UNAUTHORIZE_URL;

  if (action && action.type !== types.LOGOUT_MANUAL) {
    nextURL += getNextUrl(window.location);
  }

  window.location = nextURL;
}

export function workerSetTokenManual({ payload }) {
  const { token } = payload;
  localStorage.setItem(AUTH_TOKEN_KEY, token);
}

export function* passwordExpiredFlow(responseData: LoginResponseForm) {
  try {
    if (!responseData.password_expired) {
      return;
    }
    const { password_expired, days_to_reset, expiration_date } = responseData;

    localStorage.setItem(
      PASSWORD_EXPIRATION_FLOW_KEY,
      JSON.stringify({
        passwordExpired: password_expired,
        daysToReset: days_to_reset,
        expirationDate: expiration_date,
      }),
    );
  } catch (error) {
    yield call(captureException, error);
  }
}
export function* clearPasswordExpiredFlow() {
  try {
    localStorage.removeItem(PASSWORD_EXPIRATION_FLOW_KEY);
  } catch (error) {
    yield call(captureException, error);
  }
}

export function* workerAuthenticate(response: LoginResponseData) {
  try {
    const { data } = response;

    yield call(clearPasswordExpiredFlow);

    if (data.ticket) {
      localStorage.setItem(AUTH_TOKEN_KEY, data.ticket.token);
      yield put(authorize());
      yield put(setToken(data.ticket.token));
    }

    if (data.user) {
      localStorage.setItem(USER_ID_KEY, data.user.id);
      localStorage.setItem(
        SERVICE_TERMS,
        JSON.stringify(filterTerms(data.user.service_terms)),
      );
      yield call(dispatchHubspotEvent, 'pe5057975_login_test', {
        email: data.user.email || '',
        ...getUtmsData(),
      });
      yield call(dispatchGaEvent, 'on_login', getUtmsData());
    }

    const nextURL = getNextURLParam(window.location.search);
    let redirectURL = `/usuarios/auto-login/?auth_token=${data.auth_token}&uid=${data.uid}`;

    if (nextURL) {
      redirectURL += `&next=${nextURL}`;
    }

    if (data.password_expired) {
      yield call(passwordExpiredFlow, data);
    }

    window.location.replace(redirectURL);
  } catch (error) {
    yield call(handlerErrors, error, authenticateFailure);
  }
}

type LoginWithEmailSubmitPayload = ReturnType<typeof loginWithEmailSubmit>;
export function* workerLoginWithEmailSubmit({
  payload: { email, password, captcha },
}: LoginWithEmailSubmitPayload) {
  try {
    const authResponse = yield call(
      iclinic.auth.loginWithEmail,
      email,
      password,
      captcha,
    );

    yield call(workerAuthenticate, authResponse);
  } catch (error) {
    if (error?.response.data) {
      if (error.response.data.message.captcha_required) {
        yield put(showCaptcha());
      } else {
        yield put(hideCaptcha());
      }
      yield put(loginWithEmailFailure([errorsMessages.notAuthenticated]));
    }

    yield put(loginWithEmailFailure([errorsMessages.loginInvalid]));
  }
}

/**
 * Authentication with Google Token
 */
export function* wokerLoginWithGoogleRequest() {
  let signInData: SocialPayload;
  try {
    signInData = yield call(getSocialSignupInfo);
    // TODO: verificar se existe o token
    const authResponse = yield call(
      iclinic.auth.loginWithGoogle,
      signInData.token,
    );
    yield call(workerAuthenticate, authResponse);
    if (process.env.MIGRATION !== 'true') {
      yield put(loginWithGoogleAuth2Success());
    }
  } catch (error) {
    if (process.env.MIGRATION === 'true') {
      if (error.response && error.response.data) {
        if (error.response.data.message.email) {
          yield put(socialSignupSuccess(signInData));

          yield call([history, 'push'], '/new/usuarios/setup/email');
        } else {
          yield put(
            loginWithGoogleAuth2Failure({
              _error: 'Dados inválidos ao entrar com o Google',
            }),
          );
        }
      } else {
        yield put(
          loginWithGoogleAuth2Failure({
            _error: 'Não foi possivel entrar com o Google',
          }),
        );
      }
    } else {
      yield put(
        loginWithGoogleAuth2Failure({
          _error: 'Não implementado no Webapp',
        }),
      );
    }
  }
}

export function* watchAuthFlow() {
  yield takeLatest(types.LOGIN_WITH_EMAIL_SUBMIT, workerLoginWithEmailSubmit);
  yield takeLatest(
    types.LOGIN_WITH_GOOGLE_AUTH2_REQUEST,
    wokerLoginWithGoogleRequest,
  );
  yield takeLatest(types.LOGOUT_MANUAL, workerUnauthorize);
  yield takeLatest(types.AUTHORIZATION_SET_TOKEN_MANUAL, workerSetTokenManual);
}

export default function* auth2Sagas() {
  yield all([fork(watchAuthFlow)]);
}
